import React, { useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { numberToCurrency } from 'vqp-common';

import { Icon, IconButton, ConfirmModal } from '../common';
import { createOrEditCategory } from '../../redux/actions/products';
import { ProductModal } from './ProductModal';
import { PRODUCT_STATUS } from '../../constants/product';

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  margin-bottom: 12px;
  overflow: hidden;
  border: 1px solid ${(props) => props.theme.colors?.lineGrey || '#e8e8e8'};
`;

const Header = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 16px 20px;
  background: white;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease;

  &:hover {
    background: ${(props) => props.theme.colors?.lightGrey || '#f5f5f5'};
  }
`;

const Chevron = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  transition: transform 0.2s ease;
  transform: rotate(${(props) => (props.$isOpen ? '-90deg' : '0deg')});

  & svg {
    width: 20px;
    height: 20px;
  }
`;

const TitleInput = styled.input`
  border: none;
  font-size: 16px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.textBold || '#333'};
  background: transparent;
  flex: 1;
  min-width: 0;
  padding: 0 4px;
  margin: 0 -4px;

  &:focus {
    outline: none;
    background: ${(props) => props.theme.colors?.lightGrey || '#f0f0f0'};
    border-radius: 4px;
  }
`;

const TitleText = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.textBold || '#333'};
  flex: 1;
  min-width: 0;
`;

const ProductCountPill = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.colors.textPrimary || '#666'};
  background: ${(props) => props.theme.colors?.lightGrey || '#eee'};
  padding: 4px 10px;
  border-radius: 12px;
  margin-right: 16px;
  margin-left: 8px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  & button {
    padding: 6px;
  }
`;

const Body = styled.div`
  overflow: hidden;
  transition: max-height 0.25s ease;
  max-height: ${(props) => (props.$isOpen ? '2000px' : '0')};
`;

const TableWrap = styled.div`
  padding: 0 20px 20px;
  border-top: 1px solid ${(props) => props.theme.colors?.lineGrey || '#e8e8e8'};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  table-layout: fixed;
`;

const Thead = styled.thead`
  & th {
    text-align: left;
    padding: 12px 12px 12px 0;
    font-size: 12px;
    font-weight: 600;
    color: ${(props) => props.theme.colors.textPrimary || '#666'};
    text-transform: none;
    border-bottom: 1px solid ${(props) => props.theme.colors?.lineGrey || '#e8e8e8'};
  }
  & th:first-of-type {
    width: 10%;
  }
  & th:nth-of-type(2) {
    width: 32%;
  }
  & th:nth-of-type(3) {
    width: 14%;
    text-align: left;
  }
  & th:nth-of-type(4) {
    width: 14%;
    text-align: left;
  }
`;

const ProductCode = styled.div`
  font-size: 13px;
  color: ${(props) => props.theme.colors?.textPrimary || '#666'};
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Tbody = styled.tbody`
  & tr {
    border-bottom: 1px solid ${(props) => props.theme.colors?.lineGrey || '#eaeaea'};
    min-height: 52px;
    height: 52px;
  }
  & tr:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: 12px 12px 12px 0;
  vertical-align: middle;
  height: 52px;
  min-height: 52px;
  box-sizing: border-box;
`;

const ProductName = styled.div`
  font-weight: 500;
  color: ${(props) => props.theme.colors.textBold || '#333'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

const StatusPill = styled.span`
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;
  flex-shrink: 0;
  background: ${(props) =>
    props.$active
      ? (props.theme.colors?.success || '#28a745')
      : (props.theme.colors?.lightGrey || '#e9ecef')};
  color: ${(props) => (props.$active ? 'white' : (props.theme.colors?.textPrimary || '#6c757d'))};
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;

  & button {
    padding: 6px;
  }
`;

const TdStatus = styled(Td)`
  text-align: left;
`;

const TdActions = styled(Td)`
  text-align: right;
`;

const AddProductRow = styled.tr`
  & td {
    padding: 12px 0 0;
    border-bottom: none;
  }
`;

const AddProductButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: ${(props) => props.theme.colors.primary};
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 0;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

export const CategoryAccordion = ({
  id,
  name,
  status,
  products,
  isOpen,
  onToggle,
  onDeleteCategory,
  onEditProduct,
  onDeleteProduct,
  onChangeProductStatus,
  onChangeCategoryStatus,
  refetchCategories,
  storeId,
}) => {
  const dispatch = useDispatch();
  const [currentName, setCurrentName] = useState(name);
  const [editingName, setEditingName] = useState(false);

  React.useEffect(() => {
    setCurrentName(name);
  }, [name]);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(undefined);
  const [deleteConfirm, setDeleteConfirm] = useState({ type: null, productId: null });

  const handleBlurName = () => {
    setEditingName(false);
    if (currentName.trim() && currentName !== name) {
      dispatch(createOrEditCategory({ category: id, name: currentName.trim(), storeId }));
    } else {
      setCurrentName(name);
    }
  };

  const confirmDeleteCategory = (e) => {
    e.stopPropagation();
    setDeleteConfirm({ type: 'category', productId: null });
  };

  const handleEditCategory = (e) => {
    e.stopPropagation();
    setEditingName(true);
  };

  const handleChangeCategoryStatus = (e) => {
    e.stopPropagation();
    if (onChangeCategoryStatus) onChangeCategoryStatus(id);
  };

  const handleAddProduct = (e) => {
    e.stopPropagation();
    setSelectedProductId(undefined);
    setProductModalOpen(true);
  };

  const handleEditProduct = (productId) => {
    setSelectedProductId(productId);
    setProductModalOpen(true);
  };

  const handleDeleteProduct = (productId) => {
    setDeleteConfirm({ type: 'product', productId });
  };

  const handleCloseProductModal = (edited) => {
    if (edited) refetchCategories();
    setProductModalOpen(false);
  };

  const productCount = products?.length ?? 0;

  return (
    <Card>
      <Header type="button" onClick={onToggle}>
        <Chevron $isOpen={isOpen}>
          <Icon name="arrowRight" width="20px" height="20px" stroke="primary" />
        </Chevron>
        {editingName ? (
          <TitleInput
            value={currentName}
            onChange={(e) => setCurrentName(e.target.value)}
            onBlur={handleBlurName}
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
        ) : (
          <TitleText>{currentName}</TitleText>
        )}
        <StatusPill $active={status === PRODUCT_STATUS.ACTIVE}>
          {status === PRODUCT_STATUS.ACTIVE ? 'Ativo' : 'Inativo'}
        </StatusPill>
        <ProductCountPill>{productCount} produto{productCount !== 1 ? 's' : ''}</ProductCountPill>
        <HeaderActions onClick={(e) => e.stopPropagation()}>
          <IconButton
            icon={status === PRODUCT_STATUS.ACTIVE ? 'pause' : 'plusBlue'}
            onClick={handleChangeCategoryStatus}
            title={status === PRODUCT_STATUS.ACTIVE ? 'Desativar categoria' : 'Ativar categoria'}
          />
          <IconButton icon="edit" onClick={handleEditCategory} title="Editar categoria" />
          <IconButton icon="delete" onClick={confirmDeleteCategory} title="Excluir categoria" />
        </HeaderActions>
      </Header>

      <Body $isOpen={isOpen}>
        <TableWrap>
          <Table>
            <Thead>
              <tr>
                <th>Código</th>
                <th>Produto</th>
                <th>Preço</th>
                <th>Status</th>
                <th style={{ width: 120, textAlign: 'right' }}>Ações</th>
              </tr>
            </Thead>
            <Tbody>
              {products?.map((product) => (
                <tr key={product.id}>
                  <Td>
                    <ProductCode title={product.code ?? ''}>{product.code ?? '—'}</ProductCode>
                  </Td>
                  <Td>
                    <ProductName title={product.name}>{product.name}</ProductName>
                  </Td>
                  <Td>{numberToCurrency(product.price)}</Td>
                  <TdStatus>
                    <StatusPill $active={product.status === PRODUCT_STATUS.ACTIVE}>
                      {product.status === PRODUCT_STATUS.ACTIVE ? 'Ativo' : 'Inativo'}
                    </StatusPill>
                  </TdStatus>
                  <TdActions>
                    <RowActions>
                      <IconButton
                        icon={product.status === PRODUCT_STATUS.ACTIVE ? 'pause' : 'plusBlue'}
                        onClick={() => onChangeProductStatus(product.id)}
                        title={product.status === PRODUCT_STATUS.ACTIVE ? 'Desativar' : 'Ativar'}
                      />
                      <IconButton
                        icon="edit"
                        onClick={() => handleEditProduct(product.id)}
                        title="Editar"
                      />
                      <IconButton
                        icon="delete"
                        onClick={() => handleDeleteProduct(product.id)}
                        title="Excluir"
                      />
                    </RowActions>
                  </TdActions>
                </tr>
              ))}
              <AddProductRow>
                <Td colSpan={5}>
                  <AddProductButton type="button" onClick={handleAddProduct}>
                    <Icon name="plus" width="16px" height="16px" />
                    Adicionar produto
                  </AddProductButton>
                </Td>
              </AddProductRow>
            </Tbody>
          </Table>
        </TableWrap>
      </Body>

      <ProductModal
        category={id}
        isOpen={productModalOpen}
        productId={selectedProductId}
        onClose={handleCloseProductModal}
        onUpdate={refetchCategories}
        storeId={storeId}
      />
      <ConfirmModal
        isOpen={deleteConfirm.type === 'category'}
        onClose={() => setDeleteConfirm({ type: null, productId: null })}
        onConfirm={onDeleteCategory}
        title="Excluir categoria"
        message="Tem certeza que deseja excluir esta categoria?"
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="danger"
      />
      <ConfirmModal
        isOpen={deleteConfirm.type === 'product'}
        onClose={() => setDeleteConfirm({ type: null, productId: null })}
        onConfirm={() => onDeleteProduct(deleteConfirm.productId, storeId)}
        title="Excluir produto"
        message="Tem certeza que deseja excluir este produto?"
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="danger"
      />
    </Card>
  );
};
